import { useFormikContext } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components.
import { IFacetProps } from 'components/Facets/types';
import { Typeahead } from 'components/Typeahead';
import { StyledAccordion, StyledAccordionDetails, StyledAccordionSummary } from 'hooks/StyledComponents';
import { styled } from '@mui/material/styles';

const StyledTypeahead = styled(Typeahead)({
  width: '100%',
});

export const TypeaheadFacet = (props: IFacetProps): ReactElement => {
  const { facet, isDisabled, isExpanded, expandIcon, onToggleExpand } = props;
  const { asArray, displayValue, payloadValue, reduxAction, reduxSelector, transformTypeaheadData } = facet;
  const dispatch = useDispatch();
  const data = useSelector(reduxSelector);
  const options = useMemo(() => transformTypeaheadData(data), [data, transformTypeaheadData]);

  const { setFieldValue, values } = useFormikContext();

  useEffect(() => {
    if (reduxAction) {
      dispatch(reduxAction() as any);
    }
  }, [dispatch, reduxAction]);

  const handleInputChange = useCallback(
    (inputValue: string) => {
      // If `asArray` is specified, we will set the formik model to be an array. The BE sometimes wants things from
      // the typeahead to be a string, other times it expects an array.
      const value: string | string[] = asArray ? [inputValue] : inputValue;

      setFieldValue(payloadValue, value);
    },
    [asArray, payloadValue, setFieldValue],
  );

  return (
    <StyledAccordion key={displayValue} disabled={isDisabled} expanded={isExpanded} onChange={onToggleExpand}>
      <StyledAccordionSummary
        aria-controls={`${displayValue}-content`}
        expandIcon={expandIcon}
        id={`${displayValue}-header`}
      >
        {displayValue}
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <StyledTypeahead
          asArray={asArray}
          inputValue={values[payloadValue] || ''}
          options={options}
          onInputChange={handleInputChange}
        />
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};
